<script setup>
    import { ref, onMounted, computed } from "vue";
    import { useStore } from 'vuex';
    import { useRouter } from 'vue-router';
    import { storage } from '@/firebase';
    import { ref as firebaseRef, uploadBytes, getDownloadURL } from 'firebase/storage';
    import Users from '@/api/users';
    import { hasPermission } from '@/tools/PermissionChecker';

    // import CreateEntity from "@/components/modals/CreateEntity.vue";
    import CreateEntity from "@/components/modals/CreateEntity-new.vue";
    import CreateUser from "@/components/modals/CreateUser.vue";
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
    import GenericTable2 from '@/components/tables/GenericTable2.vue';
    import ConfirmModal from "@/components/modals/ConfirmModal.vue";


    const entities = ref({});
    const selectedUser = ref('');
    const selectedEntity = ref({});
    const userMenu = ref(null);
    const modal = ref(null);
    const loading = ref(true);
    const showDeleteModal = ref(false);
    const pendingDeleteUID = ref(null);
    const deleteLoading = ref(false);
    const deleteError = ref('');
    const pendingDeleteEntityID = ref(null);
    const openEntityID = ref(null);

    const toggleEntity = (id) => {
        openEntityID.value = openEntityID.value === id ? null : id;
    };
    // Grab stored values
    const store = useStore();
    const router = useRouter();
    const user = store.state.user;

    // Permission checks
    const canCreateEntity = computed(() => hasPermission('createEntity'));
    const canEditEntity = computed(() => hasPermission('editEntity'));
    const canAddEntityUser = computed(() => hasPermission('addEntityUser'));
    const canEditEntityUser = computed(() => hasPermission('editEntityUser'));
    const canDeleteEntityUser = computed(() => hasPermission('deleteEntityUser'));
    
    // Grant view access if user has ANY entity-related permission
    // (because you need to view entities to perform any operations on them)
    const hasViewEntitiesAccess = computed(() => 
        hasPermission('viewEntities') ||
        canCreateEntity.value ||
        canEditEntity.value ||
        canAddEntityUser.value ||
        canEditEntityUser.value ||
        canDeleteEntityUser.value
    );

    // Computed array of available actions based on permissions
    const availableActions = computed(() => {
        const actions = [];
        if (canEditEntityUser.value) actions.push('edit');
        if (canDeleteEntityUser.value) actions.push('delete');
        return actions;
    });

    const goBack = () => {
        router.push({ name: 'Home' });
    };

    onMounted(async() => {
        loading.value = true;
        try {
            // Grab the sub-entities.
            for (let entity of user.entityData[user.entity].subEntities) {
                if (entity != user.entity && user.entityData[entity]) {
                    entities.value[entity] = {name: user.entityData[entity].name, users:[], tableData:[]};

                    const response = await Users.get(`?entity=${entity}`);
                    if (response.status === 'success') {
                        entities.value[entity].users = response.details.users;
                        for (let user of response.details.users) {
                            entities.value[entity].tableData.push({
                                id: user.id,
                                Users: user.firstName + ' ' + user.lastName,
                                Email: user.email,
                                Role: user.role.replace(/-/g, ' '),
                                Status: user.status
                            });
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error loading entities:', error);
        } finally {
            loading.value = false;
        }
    });
    
    // const toggleUserMenu = (id) => {
    //     if (userMenu.value === id) {
    //         userMenu.value = null;
    //     } else {
    //         userMenu.value = id;
    //     }
    // };

    const handleActionSelected = (actionData, entityID) => {
        const { action, row } = actionData;
        
        // Set the selected entity for both edit and delete actions
        selectedEntity.value = { id: entityID };
        
        if (action === 'edit') {
            // Find the full user object from the entity's users array
            const fullUserData = entities.value[entityID].users.find(user => user.id === row.id);
            selectedUser.value = fullUserData;
            modal.value = 'editUserModal';
        }
        else if (action === 'delete') {
            deleteUser(row.id);
        }
    };

    const onEntityCreated = ({ entityId, name }) => {
        entities.value[entityId] = { name, users: [], tableData: [] };
    };

    const uploadImage = async (folder, file) => {
        if (!file) return "";
        let imageURL = null;
        const storageRef = firebaseRef(storage, `${folder}/${file.name}`);
        await uploadBytes(storageRef, file).then(() => {
            return getDownloadURL(storageRef);
        }).then((url) => {
            imageURL = url;
        });
        return imageURL;
    };

    const createUser = async (data) => {
        if (!canAddEntityUser.value) {
            alert('You do not have permission to add users to entities.');
            return;
        }

        let responseData;

        const body = {
            id: user.id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            role: data.role,
            entity: selectedEntity.value.id,
            company: data.company,
            timeFormat: data.timeFormat || "24Hour",
            permissions: data.permissions || [],
            units: data.units || [],
            profilePic: await uploadImage('profile', data.profilePic) || "",
            status: 'active'
        }

        try {
            responseData = await Users.post('', body);
        } catch (error) {
            console.error('Error creating user:', error);
        }

        modal.value = null;
        if (responseData.status === 'success') {
            body.id = responseData.details.newUserUID;
            entities.value[selectedEntity.value.id].users.push(body);
            
            // Add to tableData so it appears in the table immediately
            entities.value[selectedEntity.value.id].tableData.push({
                id: body.id,
                Users: body.firstName + ' ' + body.lastName,
                Email: body.email,
                Role: body.role.replace(/-/g, ' '),
                Status: body.status
            });
        }
    };

    const editUser = async (data) => {
        if (!canEditEntityUser.value) {
            alert('You do not have permission to edit entity users.');
            return;
        }

        userMenu.value = null;
        let responseData;

        const body = {
            id: user.id,
            userUID: selectedUser.value.id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            role: data.role,
            entity: selectedEntity.value.id,
            company: data.company,
            timeFormat: data.timeFormat || "24Hour",
            permissions: data.permissions || [],
            units: data.units || [],
            profilePic: await uploadImage('profile', data.profilePic) || selectedUser.value.profilePic || "",
            status: 'active'
        }

        try {
            responseData = await Users.put('', body);
        } catch (error) {
            console.error('Error updating user:', error);
        }

        modal.value = null;
        if (responseData.status === 'success') {
            // Update the users array
            const index = entities.value[selectedEntity.value.id].users.findIndex(map => map.id === selectedUser.value.id);
            if (index !== -1) {
                entities.value[selectedEntity.value.id].users[index] = {
                    ...entities.value[selectedEntity.value.id].users[index],
                    ...body,
                    id: selectedUser.value.id
                };
                
                // Update the tableData
                const tableIndex = entities.value[selectedEntity.value.id].tableData.findIndex(map => map.id === selectedUser.value.id);
                if (tableIndex !== -1) {
                    entities.value[selectedEntity.value.id].tableData[tableIndex] = {
                        id: selectedUser.value.id,
                        Users: body.firstName + ' ' + body.lastName,
                        Email: body.email,
                        Role: body.role.replace(/-/g, ' '),
                        Status: body.status
                    };
                }
            }
            
            // setTimeout(() => {
            //     // alert("User updated successfully!");
            // }, 100);
        } else {
            setTimeout(() => {
                try {
                    alert(responseData.message || "Something went wrong when updating the user");
                } catch (error) {
                    alert("Something went wrong when updating the user");
                }
            }, 100);
        }
    };

    const handleUserFormSubmit = async (data) => {
        if (modal.value === 'editUserModal') {
            await editUser(data);
        } else {
            await createUser(data);
        }
    };

    const deleteUser = (userUID) => {
        if (!canDeleteEntityUser.value) {
            alert('You do not have permission to delete entity users.');
            return;
        }
        if (userUID === user.id) {
            alert("Can't delete yourself");
            return;
        }
        userMenu.value = null;
        pendingDeleteUID.value = userUID;
        pendingDeleteEntityID.value = selectedEntity.value.id;
        deleteError.value = '';
        showDeleteModal.value = true;
    };

    const confirmDeleteEntityUser = async () => {
        deleteLoading.value = true;
        deleteError.value = '';
        const entityID = pendingDeleteEntityID.value;
        const body = { userUID: [pendingDeleteUID.value] };
        let responseData;
        try {
            responseData = await Users.delete('', body);
        } catch (error) {
            console.error('Error deleting user:', error);
            deleteError.value = 'Something went wrong when deleting the user';
            deleteLoading.value = false;
            return;
        }
        deleteLoading.value = false;
        if (responseData.status === 'success') {
            entities.value[entityID].users = entities.value[entityID].users.filter(u => u.id !== pendingDeleteUID.value);
            entities.value[entityID].tableData = entities.value[entityID].tableData.filter(u => u.id !== pendingDeleteUID.value);
            showDeleteModal.value = false;
            pendingDeleteUID.value = null;
        } else {
            deleteError.value = responseData.message || 'Something went wrong when deleting the user';
        }
    };

    const cancelDeleteEntityUser = () => {
        showDeleteModal.value = false;
        pendingDeleteUID.value = null;
        pendingDeleteEntityID.value = null;
        deleteError.value = '';
    };
</script>

<template>
    <!-- Access Denied Screen -->
    <div v-if="!hasViewEntitiesAccess" class="flex flex-col items-center justify-center h-full p-8">
        <div class="max-w-md text-center">
            <h1 class="text-4xl font-bold text-mc-red mb-4">
                Access Denied
            </h1>
            <p class="text-xl text-ink-muted dark:text-on-dark-muted mb-2">
                You do not have permission to view entities.
            </p>
            <p class="text-lg text-ink-muted dark:text-on-dark-muted mb-6">
                Required permission:
                <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">viewEntities</span>
                or admin role
            </p>
            <button @click="goBack" class="default-button">Go Back to Home</button>
        </div>
    </div>

    <!-- Protected Content -->
    <div v-else>
        <!-- Page header -->
        <div class="flex items-center justify-between mt-4 mb-6">
            <h1 class="text-2xl font-bold text-mc-ink dark:text-on-dark">Entities</h1>
            <button v-if="canCreateEntity" @click="modal = 'createEntityModal'" class="default-button w-fit">
                + Add Entity
            </button>
        </div>

        <div v-if="loading" class="flex flex-col items-center mt-10">
            <LoadingAnimation3 message="Retrieving Entities. This may take a minute..." />
        </div>

        <div v-else class="flex flex-col gap-2">
            <div
                v-for="(entityData, entityID) in entities"
                :key="entityID"
                class="rounded-mc-sm overflow-hidden shadow-mc-sm"
            >
                <!-- Header row -->
                <div
                    class="w-full bg-white dark:bg-mc-charcoal flex items-center justify-between border-l-[3px] transition-colors duration-160"
                    :class="openEntityID === entityID ? 'border-mc-red' : 'border-transparent'"
                >
                    <button
                        @click="toggleEntity(entityID)"
                        :aria-expanded="openEntityID === entityID"
                        class="flex-1 px-4 py-3 flex items-center gap-2.5 text-left"
                    >
                        <svg
                            aria-hidden="true"
                            class="w-3 h-3 flex-shrink-0 transition-transform duration-160"
                            :class="openEntityID === entityID ? 'rotate-180 text-mc-red' : 'text-ink-muted dark:text-on-dark-muted'"
                            fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                        <span
                            class="font-bold text-sm transition-colors duration-160"
                            :class="openEntityID === entityID ? 'text-mc-ink dark:text-on-dark' : 'text-ink-muted dark:text-on-dark-muted'"
                        >{{ entityData.name }}</span>
                        <span
                            class="rounded-full px-2 py-0.5 text-xs font-semibold transition-colors duration-160"
                            :class="openEntityID === entityID
                                ? 'bg-mc-red-100 text-mc-red'
                                : 'bg-mc-steel/50 text-ink-muted dark:bg-white/10 dark:text-on-dark-muted'"
                        >{{ entityData.users.length }} users</span>
                    </button>
                    <div
                        v-if="openEntityID === entityID"
                        class="flex items-center gap-2.5 pr-4"
                    >
                        <button
                            v-if="canEditEntity"
                            @click="modal = 'createEntityModal'; selectedEntity = user.entityData[entityID]; selectedEntity.id = entityID"
                            class="text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark transition-colors duration-160 text-sm flex items-center gap-1"
                        >
                            <font-awesome-icon icon="fa-edit" /> Edit
                        </button>
                        <button
                            v-if="canAddEntityUser"
                            data-testid="add-user-btn"
                            @click="modal = 'createUserModal'; selectedEntity.id = entityID"
                            class="default-button text-xs py-1.5"
                        >
                            + Add User
                        </button>
                    </div>
                </div>

                <!-- Expanded content -->
                <Transition name="accordion">
                    <div v-if="openEntityID === entityID">
                        <div class="min-h-0 border-l-[3px] border-l-mc-red border-t border-t-mc-steel dark:border-t-line-on-dark px-4 py-3">
                            <div
                                v-if="entityData.tableData.length === 0"
                                data-testid="empty-state"
                                class="flex flex-col items-center gap-3 py-6 text-ink-muted dark:text-on-dark-muted"
                            >
                                <p class="text-sm">No users yet.</p>
                                <button
                                    v-if="canAddEntityUser"
                                    @click="modal = 'createUserModal'; selectedEntity.id = entityID"
                                    class="default-button text-xs py-1.5"
                                >
                                    + Add First User
                                </button>
                            </div>
                            <GenericTable2
                                v-else
                                :data="entityData.tableData"
                                :showId="false"
                                :actions="availableActions"
                                :responsive="true"
                                @ActionSelected="(actionData) => handleActionSelected(actionData, entityID)"
                            />
                        </div>
                    </div>
                </Transition>
            </div>
        </div>

        <CreateEntity
            v-if="modal === 'createEntityModal'"
            :entityDetails="selectedEntity"
            @close="modal = null; selectedEntity = {}"
            @entity-created="onEntityCreated"
        />
        <CreateUser
            v-if="modal === 'createUserModal' || modal === 'editUserModal'"
            :userDetails="modal === 'editUserModal' ? selectedUser : null"
            @close="modal = null; selectedUser = ''"
            @submitForm="handleUserFormSubmit"
        />
        <ConfirmModal
            v-if="showDeleteModal"
            title="Delete User"
            message="Are you sure you want to delete this user?"
            confirm-label="Delete"
            variant="danger"
            :loading="deleteLoading"
            :error="deleteError"
            @confirm="confirmDeleteEntityUser"
            @cancel="cancelDeleteEntityUser"
        />
    </div>
</template>

<style>
.accordion-enter-active,
.accordion-leave-active {
    display: grid;
    grid-template-rows: 1fr;
    overflow: hidden;
    transition: grid-template-rows 180ms ease;
}

.accordion-enter-from,
.accordion-leave-to {
    grid-template-rows: 0fr;
}
</style>
